import React from 'react';
import { Document, Image, Page, PDFViewer, StyleSheet, Text, View } from '@react-pdf/renderer';
import { format } from "date-fns";
import { useEffect, useState } from 'react';
import loadingLoader from './loading-loader.svg'; // ajuste caminho se necessário

// Função utilitária para decodificar HTML entities e preservar quebras de linha
const decodeHtmlEntities = (html) => {
  if (!html) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  let value = txt.value;
  value = value.replace(/<br\s*\/?>/gi, '\n'); // <br> para \n
  value = value.replace(/<[^>]+>/g, ''); // remover tags HTML restantes
  return value;
};

// Estilos PDF
const styles = StyleSheet.create({
  page: { flexDirection: 'column', flex: 1, fontSize: 10, padding: 10 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  logoContainer: { flex: 1, alignItems: 'flex-start' },
  logo: { height: 60 },
  headerTextContainer: { flex: 2, alignItems: 'flex-end' },
  headerText: { fontSize: 10, fontWeight: 'bold' },
  titleBar: { backgroundColor: '#666', color: '#fff', textAlign: 'center', fontSize: 10, fontWeight: 'bold', padding: 8, marginBottom: 20 },
  section: { marginBottom: 20 },
  footer: { width: '100%', textAlign: 'center', fontSize: 9, color: '#888', borderTopWidth: 1, borderColor: '#eee', paddingTop: 8, paddingBottom: 10, marginTop: 20, flexDirection: 'column', alignItems: 'center' },
  footerImage: { marginTop: 6, width: '100%', maxHeight: 100, objectFit: 'contain' },
  boldText: { fontWeight: 'bold' },
  row: { flexDirection: 'row', marginBottom: 2 },
  cell: { padding: 4 },
  tableHeader: { flexDirection: 'row', borderBottomWidth: 1, borderColor: '#000', backgroundColor: '#f5f5f5' }
});

// Formata números para pt-PT
function formatPtNumber(value) {
  if (value === '' || value === null || value === undefined) return '';
  let str = (value + '').replace(/[^\d.,]/g, '');
  let num = NaN;
  if (str.includes(',') && str.includes('.')) {
    if (str.lastIndexOf(',') > str.lastIndexOf('.')) num = parseFloat(str.replace(/\./g, '').replace(',', '.'));
    else num = parseFloat(str.replace(/,/g, ''));
  } else if (str.includes(',')) num = parseFloat(str.replace(/\./g, '').replace(',', '.'));
  else num = parseFloat(str.replace(/\./g, ''));
  if (isNaN(num)) num = 0;
  let formatted = new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(num).replace(/\s/g, '.');
  formatted = formatted.replace('EUR', '').replace('.€', '€').trim();
  return formatted;
}

// Renderiza cada resposta individual
const renderAnswerRow = (answer, index, acoes) => {
  if (!answer?.question) return null;
  const { name: questionName, type, editor_html_c, json_c } = answer.question;
  const questionTitle = questionName ? decodeHtmlEntities(questionName) : '';

  return (
    <View key={index} wrap={false} style={{ marginBottom: 12 }}>
      {questionTitle && <Text style={{ ...styles.boldText, marginBottom: 4, borderBottomWidth: 1, borderColor: '#000' }}>{questionTitle}</Text>}
      {(() => {
        switch (type) {
          case 'Text': {
            const decoded = decodeHtmlEntities(editor_html_c);
            return <Text>{decoded}</Text>;
          }
          case 'Textbox': {
            let value = '';
            try {
              const decoded = decodeHtmlEntities(answer.answer?.answer || '');
              value = JSON.parse(decoded)?.value || '';
            } catch { value = ''; }
            return <Text>{value}</Text>;
          }
          case 'Date': {
            const dateStr = answer.answer?.answer_datetime || answer.value || '';
            let formatted = dateStr;
            try {
              const d = new Date(dateStr);
              if (!isNaN(d)) formatted = format(d, "dd/MM/yyyy");
            } catch { }
            return <Text>{formatted}</Text>;
          }
          case 'multiple_fields': {
            let config = {};
            try { config = JSON.parse(decodeHtmlEntities(json_c || '')); } catch { config = {}; }
            let answersArr = [];
            try { answersArr = JSON.parse(decodeHtmlEntities(answer.answer?.answer || '')); } catch { answersArr = []; }

            return (
              <View>
                {Object.entries(config).map(([fieldKey, fieldConfig], idx) => (
                  <View key={idx} style={{ flexDirection: 'row', marginBottom: 2 }}>
                    <Text style={{ fontWeight: 'bold', paddingRight: 8, maxWidth: '50%' }}>
                      {decodeHtmlEntities(fieldConfig.name || fieldKey)}:
                    </Text>
                    <Text>{answersArr[idx] ?? ''}</Text>
                  </View>
                ))}
              </View>
            );
          }
          case 'Signature': {
            let config = {};
            try { config = JSON.parse(decodeHtmlEntities(json_c || '')); } catch { config = {}; }
            const alignment = config.alignment || 'center';
            const desc1 = decodeHtmlEntities(config.description1 || 'O Representante da Empresa');
            const desc2 = decodeHtmlEntities(config.description2 || '(assinatura e carimbo da empresa)');
            let alignStyle = {};
            if (alignment === 'left') alignStyle.textAlign = 'left';
            else if (alignment === 'right') alignStyle.textAlign = 'right';
            else alignStyle.textAlign = 'center';

            return (
              <View style={{ marginTop: 40, marginBottom: 20 }}>
                <View style={{ borderBottomWidth: 1, borderColor: '#000', width: '50%', alignSelf: alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center', marginBottom: 8 }} />
                <Text style={{ ...alignStyle }}>{desc1}</Text>
                <Text style={{ ...alignStyle }}>{desc2}</Text>
              </View>
            );
          }
          case 'table_acoes': {
            let config = {};
            try { config = JSON.parse(decodeHtmlEntities(json_c || '')); } catch { config = {}; }
            const columns_acao = Array.isArray(config.columns_acao) && config.columns_acao.length ? config.columns_acao : [{ field: "name", label: "Domínios Imateriais de Competitividade", width: 1 }];
            const columns_inv = Array.isArray(config.columns_inv) && config.columns_inv.length ? config.columns_inv : [{ field: "name", label: "Investimento", width: 1 }, { field: "escalao_c", label: "Valor", width: 1 }, { field: "anos_c", label: "Ano", width: 1 }];
            let answersArr = [];
            try { answersArr = JSON.parse(decodeHtmlEntities(answer.answer?.answer || '')); } catch { answersArr = []; }
            const actions_array = Array.isArray(config.actions) ? config.actions : [];
            const actions = actions_array.map(actionId => acoes.find(ele => ele.id === actionId)).filter(Boolean);

            return (
              <View style={{ marginVertical: 10 }}>
                {actions.map((acao, acaoIdx) => {
                  const acaoAnswer = answersArr[acao.id] || {};
                  const investimentos = Array.isArray(acao.investimentos) ? acao.investimentos : [];

                  return (
                    <View key={acaoIdx}>
                      <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#ccc' }}>
                        {columns_acao.map((col, idx) => (
                          <Text key={idx} style={{ flex: col.width || 1, padding: 4 }}>
                            {decodeHtmlEntities(acao[col.field] ?? '')}
                          </Text>
                        ))}
                        <Text style={{ flex: 1, padding: 4 }}>
                          {acaoAnswer.checked ? 'Sim' : 'Não'}
                        </Text>
                      </View>
                      {acaoAnswer.checked && investimentos.length > 0 && (
                        <View style={{ paddingLeft: 10, paddingBottom: 6 }}>
                          <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#ddd' }}>
                            {columns_inv.map((col, idx) => (
                              <Text key={idx} style={{ flex: col.width || 1, fontWeight: 'bold', padding: 4 }}>
                                {decodeHtmlEntities(col.label || col.field)}
                              </Text>
                            ))}
                          </View>
                          {investimentos.map((inv, invIdx) => {
                            let extraFieldsInvestimento = [];
                            let escalaoLabels = {};
                            if (inv.json_escaloes_c) {
                              try { escalaoLabels = JSON.parse(decodeHtmlEntities(inv.json_escaloes_c)); } catch { escalaoLabels = {}; }
                            }
                            if (inv.extra_fields_c) {
                              try { extraFieldsInvestimento = JSON.parse(decodeHtmlEntities(inv.extra_fields_c)); } catch { extraFieldsInvestimento = []; }
                            }

                            return (
                              <View key={invIdx}>
                                <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#eee' }}>
                                  {columns_inv.map((col, idx) => {
                                    let value = inv[col.field];
                                    if (col.field === 'escalao_c') value = formatPtNumber(Number(inv.valor_investimento_c));
                                    else if (col.field === 'anos_c') value = inv.anos_c;
                                    return <Text key={idx} style={{ flex: col.width || 1, padding: 4 }}>{decodeHtmlEntities(value ?? '')}</Text>;
                                  })}
                                </View>
                              </View>
                            );
                          })}
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            );
          }
          default: return <Text>{JSON.stringify(answer.value)}</Text>;
        }
      })()}
    </View>
  );
};

// Componente principal do PDF
const MyDocument = ({ data }) => (
  <Document title={decodeHtmlEntities(data?.candidatura?.name || '')}>
    <Page size="A4" style={styles.page}>
      <View style={styles.header} fixed>
        <View style={styles.logoContainer}>
          <Image src={"https://demo-workcrm.sw.nka.pt/download_logo.php?id=" + data.idLogo} style={styles.logo} />
        </View>
        <View style={styles.headerTextContainer}>
          <Text style={styles.headerText}>{decodeHtmlEntities(data?.candidatura?.programa_apoio_c || '')}</Text>
        </View>
      </View>

      <View style={styles.titleBar}>
        <Text>{decodeHtmlEntities(data?.nameManifestacaoInteresse || '')}</Text>
      </View>

      {Array.isArray(data?.answers) && data.answers.map((a, i) => (
        <View key={i} wrap={false} style={{ marginBottom: 16 }}> {/* aumenta margem entre ações */}
          {renderAnswerRow(a, i, data?.acoes)}
        </View>
      ))}
      
      <View style={styles.footer} fixed>
        <Text>Documento gerado automaticamente em {format(new Date(), "dd/MM/yyyy HH:mm")}</Text>
        <Image src={"https://demo-workcrm.sw.nka.pt/download_logo.php?id=" + data.idRodape} style={styles.footerImage} />
      </View>
    </Page>
  </Document>
);

// Wrapper do PDF + fetch
const SurveyPDF = () => {
  const [data, setData] = useState(null);
  const responseId = new URLSearchParams(document.location.search).get('responseId');

  useEffect(() => {
    if (!responseId) return;
    const fetchData = async () => {
      try {
        const res = await fetch(`https://demo-workcrm.sw.nka.pt/index.php?entryPoint=get_survey_response_data&response_id=${responseId}`);
        const json = await res.json();
        setData(json);
      } catch {
        setData({ error: 'Failed to fetch data' });
      }
    };
    fetchData();
  }, [responseId]);

  if (!data) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#888' }}>
      <img src={loadingLoader} alt="Carregando..." style={{ width: 180, height: 180, marginBottom: 16 }} />
    </div>
  );

  if (data.error) return <Text>Error: {data.error}</Text>;

  return <PDFViewer width="100%" height="100%">{data && <MyDocument data={data} />}</PDFViewer>;
};

export default SurveyPDF;